import type { ReactiveController, ReactiveControllerHost } from 'lit'

/**
 * A reactive controller that determines when slots exist.
 * 
 * From: https://github.com/shoelace-style/shoelace/blob/854db13bd7fbfad65f01de0838fe1a6b2dbf0a4d/src/internal/slot.ts#L3-L62
 */
export class HasSlotController implements ReactiveController {
  host: ReactiveControllerHost & Element;
  slotNames: string[] = [];

  constructor(host: ReactiveControllerHost & Element, ...slotNames: string[]) {
    (this.host = host).addController(this);
    this.slotNames = slotNames;
    this.handleSlotChange = this.handleSlotChange.bind(this);
  }

  private hasDefaultSlot() {
    return [...this.host.childNodes].some(node => {
      if (node.nodeType === node.TEXT_NODE && node.textContent!.trim() !== '') {
        return true;
      }

      if (node.nodeType === node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        //const tagName = el.tagName.toLowerCase();

        // Ignore visually hidden elements since they aren't rendered
        // if (tagName === 'sl-visually-hidden') {
        //   return false;
        // }

        // If it doesn't have a slot attribute, it's part of the default slot
        if (!el.hasAttribute('slot')) {
          return true;
        }
      }

      return false;
    });
  }

  private hasNamedSlot(name: string) {
    return this.host.querySelector(`:scope > [slot="${name}"]`) !== null;
  }

  /**
   * Tests if this element has anything slotted in the given `slotName`
   * @param slotName use `[default]` for the unnamed `<slot>`
   * @returns 
   */
  public test(slotName: string): boolean {
    return slotName === '[default]' ? this.hasDefaultSlot() : this.hasNamedSlot(slotName);
  }

  /**
   * Tests if this element has at least 1 thing slotted in ANY of the given `slotNames` slots
   * @param slotNames use `[default]` for the unnamed `<slot>`
   * @returns 
   */
  public testAny(...slotNames: string[]): boolean {
    return slotNames.some(name => this.test(name));
  }

  /**
   * Tests if this element has things slotted in ALL of the given `slotNames` slots
   * @param slotNames use `[default]` for the unnamed `<slot>`
   * @returns 
   */
  public testAll(...slotNames: string[]): boolean {
    return slotNames.every(name => this.test(name));
  }

  hostConnected() {
    this.host.shadowRoot!.addEventListener('slotchange', this.handleSlotChange);
  }

  hostDisconnected() {
    this.host.shadowRoot!.removeEventListener('slotchange', this.handleSlotChange);
  }

  protected handleSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement;

    if ((this.slotNames.includes('[default]') && !slot.name) || (slot.name && this.slotNames.includes(slot.name))) {
      this.host.requestUpdate();
    }
  }
}